import { gsap } from 'gsap';
import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';
import { renderFormulas } from '@/shared/utils/content';

import type { LaneChooserQuestionPayload } from '@/features/laneChooser/game/types';

type QuestionControllerConfig = {
	scene: Phaser.Scene;
	question: LaneChooserQuestionPayload | null;
	viewportWidth: number;
	viewportHeight: number;
	onImagePreview?: (imageSrc: string) => void;
};

const QUESTION_FADE_OFFSET_PX = 16;
const QUESTION_FADE_DURATION_SECONDS = 0.18;

const sanitizeQuestionHtml = (html: string): string => {
	return renderFormulas(html.replace(/<figure[\s\S]*?<\/figure>/gi, '').replace(/<img[^>]*>/gi, ''));
};

export class QuestionController {
	// Scene reference used to create and destroy the Phaser DOM element.
	private readonly scene: Phaser.Scene;
	// Current question payload rendered inside the Phaser DOM container.
	private question: LaneChooserQuestionPayload | null;
	// CSS viewport width used by the established question layout.
	private readonly viewportWidth: number;
	// CSS viewport height used by the established question layout.
	private readonly viewportHeight: number;
	// React-owned image preview callback bridged through the scene event bus.
	private readonly onImagePreview?: (imageSrc: string) => void;
	// Phaser DOM element that owns the question markup inside the game stage.
	private domElement: Phaser.GameObjects.DOMElement | null = null;
	// Static DOM root reused across round transitions.
	private rootElement: HTMLDivElement | null = null;
	// Active question node that fades out and in when the round changes.
	private questionNode: HTMLDivElement | null = null;
	// Image button used to trigger the preview modal for the current question image.
	private imageButton: HTMLButtonElement | null = null;
	// Stable click handler so destroy can remove the listener cleanly.
	private readonly handleImageButtonClick = () => {
		const imageSrc = this.question?.imageUrls[0];
		if (!imageSrc) {
			return;
		}

		this.onImagePreview?.(imageSrc);
	};

	constructor({ scene, question, viewportWidth, viewportHeight, onImagePreview }: QuestionControllerConfig) {
		this.scene = scene;
		this.question = question;
		this.viewportWidth = viewportWidth;
		this.viewportHeight = viewportHeight;
		this.onImagePreview = onImagePreview;

		this.createDomElement();
	}

	// Destroys the DOM element and any attached listeners when the scene rerenders or shuts down.
	destroy() {
		if (this.questionNode) {
			gsap.killTweensOf(this.questionNode);
		}
		this.imageButton?.removeEventListener('click', this.handleImageButtonClick);
		this.imageButton = null;
		this.questionNode = null;
		this.rootElement = null;
		this.domElement?.destroy();
		this.domElement = null;
	}

	// Swaps question content in place so the scene does not need a full rerender between rounds.
	setQuestion(question: LaneChooserQuestionPayload | null, animate = true) {
		this.question = question;

		if (!this.rootElement || !this.domElement) {
			this.createDomElement();
			return;
		}

		if (!animate) {
			this.replaceQuestionNode(question);
			return;
		}

		const currentQuestionNode = this.questionNode;
		if (!currentQuestionNode) {
			this.replaceQuestionNode(question);
			this.fadeInQuestionNode();
			return;
		}

		gsap.killTweensOf(currentQuestionNode);
		gsap.to(currentQuestionNode, {
			autoAlpha: 0,
			y: -QUESTION_FADE_OFFSET_PX,
			duration: QUESTION_FADE_DURATION_SECONDS,
			ease: 'power1.out',
			onComplete: () => {
				this.replaceQuestionNode(question);
				this.fadeInQuestionNode();
			},
		});
	}

	setVisible(visible: boolean) {
		this.domElement?.setVisible(visible);
	}

	// Builds the DOM root once and mounts it into the Phaser stage.
	private createDomElement() {
		const root = document.createElement('div');
		root.className = 'lane-chooser__phaser-question-layer';
		root.style.position = 'absolute';
		root.style.left = '0';
		root.style.top = '0';
		root.style.width = `${this.viewportWidth}px`;
		root.style.height = `${this.viewportHeight}px`;
		root.style.pointerEvents = 'none';
		this.rootElement = root;
		this.replaceQuestionNode(this.question);

		this.domElement = this.scene.add.dom(0, 0, root);
		this.domElement.setOrigin(0, 0);
		this.domElement.setDepth(5);
		this.domElement.setScrollFactor(0);
		this.domElement.pointerEvents = 'none';
	}

	// Rebuilds the visible question node for the current payload inside the persistent DOM root.
	private replaceQuestionNode(question: LaneChooserQuestionPayload | null) {
		this.imageButton?.removeEventListener('click', this.handleImageButtonClick);
		this.imageButton = null;
		this.questionNode?.remove();
		this.questionNode = null;

		if (!this.rootElement || !question) {
			return;
		}

		const questionNode = document.createElement('div');
		questionNode.className = 'lane-chooser__question';
		questionNode.style.visibility = 'visible';
		questionNode.style.left = '50%';
		questionNode.style.right = 'auto';
		questionNode.style.margin = '0';
		questionNode.style.transform = 'translateX(-50%)';
		questionNode.style.top =
			this.viewportWidth >= SCREEN_SIZES.TABLET ? '15vh' : 'calc(env(safe-area-inset-top) + 80px)';

		const splitNode = document.createElement('div');
		splitNode.className = 'lane-chooser__question-split';
		questionNode.appendChild(splitNode);

		const firstImage = question.imageUrls[0] ?? '';
		if (firstImage) {
			const leftNode = document.createElement('div');
			leftNode.className = 'lane-chooser__question-left';
			const imageButton = document.createElement('button');
			imageButton.type = 'button';
			imageButton.className = 'wysiwyg-content lane-chooser__question-image-btn';
			imageButton.setAttribute('aria-label', 'Open image preview');
			imageButton.style.pointerEvents = 'auto';
			const imageNode = document.createElement('img');
			imageNode.src = firstImage;
			imageNode.alt = 'Question';

			imageButton.appendChild(imageNode);
			imageButton.addEventListener('click', this.handleImageButtonClick);
			leftNode.appendChild(imageButton);
			splitNode.appendChild(leftNode);
			this.imageButton = imageButton;
		}

		const rightNode = document.createElement('div');
		rightNode.className = question.questionHasImage
			? 'lane-chooser__question-right'
			: 'lane-chooser__question-right lane-chooser__question-right--no-image';

		if (question.title) {
			const titleNode = document.createElement('h1');
			titleNode.className = 'lane-chooser__question-title';
			titleNode.textContent = question.title;
			rightNode.appendChild(titleNode);
		}

		const contentNode = document.createElement('div');
		contentNode.className = 'wysiwyg-content';
		contentNode.innerHTML = sanitizeQuestionHtml(question.questionHtml || '');
		rightNode.appendChild(contentNode);
		splitNode.appendChild(rightNode);

		questionNode.style.opacity = '1';
		questionNode.style.transform = 'translateX(-50%) translateY(0)';
		this.rootElement.appendChild(questionNode);
		this.questionNode = questionNode;
	}

	// Fades the next question into place after the previous content has been removed.
	private fadeInQuestionNode() {
		if (!this.questionNode) {
			return;
		}

		gsap.killTweensOf(this.questionNode);
		gsap.fromTo(
			this.questionNode,
			{
				autoAlpha: 0,
				y: QUESTION_FADE_OFFSET_PX,
			},
			{
				autoAlpha: 1,
				y: 0,
				duration: QUESTION_FADE_DURATION_SECONDS,
				ease: 'power1.out',
			},
		);
	}
}
